import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROOM } from "@/constants/testIds";
import {
  ArrowLeft, FileText, Users as UsersIcon, MessagesSquare, BarChart3,
  Shield, Cog, FolderLock, Brain, ListTodo, Trash2,
} from "lucide-react";

import DocumentsTab from "./dataroom/DocumentsTab";
import MembersTab from "./dataroom/MembersTab";
import QATab from "./dataroom/QATab";
import AnalyticsTab from "./dataroom/AnalyticsTab";
import PermissionsTab from "./dataroom/PermissionsTab";
import SettingsTab from "./dataroom/SettingsTab";
import EngagementTab from "./dataroom/EngagementTab";
import RecycleBinTab from "./dataroom/RecycleBinTab";
import AdaBubble from "@/components/ada/AdaBubble";

export default function DataRoom() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = user?.role === "admin";
  const [room, setRoom] = useState(null);
  const initialTab = searchParams.get("tab") || "documents";
  const [tab, setTab] = useState(initialTab);

  // Keep URL ?tab=… in sync (no history entry on every click).
  useEffect(() => {
    const qp = searchParams.get("tab");
    if (qp && qp !== tab) setTab(qp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const selectTab = (next) => {
    setTab(next);
    const sp = new URLSearchParams(searchParams);
    sp.set("tab", next);
    setSearchParams(sp, { replace: true });
  };

  useEffect(() => {
    api.get(`/rooms/${id}`).then(({ data }) => setRoom(data)).catch(() => {});
  }, [id]);

  // Open-task badge for this room. Gated by the Tasks-access flag so we
  // don't 403-spam users that don't have the feature enabled.
  const [openTasks, setOpenTasks] = useState(0);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: a } = await api.get("/tasks/access/me");
        if (!alive || !a?.has_access) return;
        const { data } = await api.get(`/tasks/rooms/${id}/counts`);
        if (alive) setOpenTasks(data?.open || 0);
      } catch {
        /* ignore — badge just stays hidden */
      }
    })();
    return () => { alive = false; };
  }, [id]);

  if (!room) return <div className="p-8 label-mono text-muted-foreground">— Loading room —</div>;

  const tabs = [
    { key: "documents", label: "Documents", icon: FileText, testId: ROOM.tabDocs },
    { key: "members", label: "Members", icon: UsersIcon, testId: ROOM.tabMembers },
    { key: "qa", label: "Q&A", icon: MessagesSquare, testId: ROOM.tabQA },
    { key: "analytics", label: "Analytics", icon: BarChart3, testId: ROOM.tabAnalytics },
    ...(isAdmin ? [
      { key: "engagement", label: "Engagement", icon: Brain, testId: "room-tab-engagement" },
      { key: "permissions", label: "Permissions", icon: Shield, testId: ROOM.tabPerms },
      { key: "recycle", label: "Recycle bin", icon: Trash2, testId: "room-tab-recycle" },
      { key: "settings", label: "Settings", icon: Cog, testId: ROOM.tabSettings },
    ] : []),
  ];

  return (
    <div className="p-8">
      <Link to="/app/rooms" className="label-mono text-muted-foreground hover:text-foreground inline-flex items-center gap-2">
        <ArrowLeft className="w-3 h-3" /> Back to rooms
      </Link>
      <div className="flex items-start justify-between mt-3">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className="w-10 h-10 rounded-md flex items-center justify-center shrink-0 mt-1.5"
            style={{ background: room.brand_color }}
          >
            <FolderLock className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="font-display text-4xl tracking-tighter font-medium truncate">{room.name}</h1>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <p className="text-sm text-muted-foreground">{room.description}</p>
              {openTasks > 0 && (
                <Link
                  to={`/app/tasks?room=${id}`}
                  data-testid="room-header-task-badge"
                  title={`${openTasks} open task${openTasks === 1 ? "" : "s"} in this room`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/15 text-primary mono text-[10px] uppercase tracking-wider hover:bg-primary/25 transition"
                >
                  <ListTodo className="w-3 h-3" />
                  {openTasks} OPEN TASK{openTasks === 1 ? "" : "S"}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-1 border-b border-border mt-8 -mx-2">
        {tabs.map((t) => (
          <button
            key={t.key}
            data-testid={t.testId}
            onClick={() => selectTab(t.key)}
            className={`px-4 py-2.5 text-sm flex items-center gap-2 border-b-2 transition ${
              tab === t.key ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "documents" && <DocumentsTab roomId={id} isAdmin={isAdmin} room={room} />}
        {tab === "members" && <MembersTab roomId={id} isAdmin={isAdmin} />}
        {tab === "qa" && <QATab roomId={id} user={user} />}
        {tab === "analytics" && <AnalyticsTab roomId={id} />}
        {tab === "engagement" && isAdmin && <EngagementTab roomId={id} />}
        {tab === "permissions" && isAdmin && <PermissionsTab roomId={id} />}
        {tab === "recycle" && isAdmin && <RecycleBinTab roomId={id} />}
        {tab === "settings" && isAdmin && <SettingsTab room={room} onSaved={(r) => setRoom(r)} navigate={navigate} />}
      </div>

      {/* Ada floating diligence copilot — bottom-right chat bubble */}
      <AdaBubble roomId={id} room={room} isAdmin={isAdmin} />
    </div>
  );
}
